import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { GET_RESERVATIONS_BY_DATE, SEARCH_RESERVATIONS } from '../graphql/queries';
import { debounce } from 'lodash';
import EditReservationModal from '../components/EditReservationModal';
import CreateWorkOrderModal from '../components/work-orders/CreateWorkOrderModal';
import { format, addDays, subDays, startOfDay, isSameDay, isToday } from 'date-fns';
import { toast } from 'react-hot-toast';
import ReservationTypeBadge from '../components/ReservationTypeBadge';
import { formatReservationDate } from '../utils/timeUtils';
import { useAuth } from '../contexts/AuthContext';

const DAYS_VISIBLE = 7;
const RESERVATIONS_PER_PAGE = 50;

const AREA_GROUPS = [
  { id: 'all', label: 'All Areas', values: [] },
  { id: 'iop', label: 'Isle of Palms', values: ['ISLE_OF_PALMS', 'ISLE_OF_PALMS_WILD_DUNES'] },
  { id: 'kiawah', label: 'Kiawah / Seabrook', values: ['KIAWAH', 'SEABROOK_ISLAND'] },
];

const getStatusDisplay = (statusCode, statusId, isCancelled) => {
  if (isCancelled) return { label: 'Cancelled', classes: 'bg-red-100 text-red-800' };

  if (statusCode) {
    switch (statusCode) {
      case '1':
        return { label: 'Blocked', classes: 'bg-purple-100 text-purple-800' };
      case '2':
        return { label: 'Not Checked In', classes: 'bg-yellow-100 text-yellow-800' };
      case '4':
        return { label: 'Modified - Not Checked In', classes: 'bg-yellow-100 text-yellow-800' };
      case '5':
        return { label: 'Checked In', classes: 'bg-emerald-100 text-emerald-800' };
      case '6':
        return { label: 'Modified - Checked In', classes: 'bg-emerald-100 text-emerald-800' };
      case '8':
        return { label: 'Checked Out', classes: 'bg-gray-100 text-gray-800' };
    }
  }

  switch (statusId) {
    case '1':
      return { label: 'Not Completed', classes: 'bg-gray-100 text-gray-800' };
    case '3':
      return { label: 'Payment Pending', classes: 'bg-orange-100 text-orange-800' };
    case '4':
      return { label: 'Booked', classes: 'bg-green-100 text-green-800' };
    case '5':
      return { label: 'Closed', classes: 'bg-gray-100 text-gray-800' };
    case '6':
      return { label: 'Deleted', classes: 'bg-red-100 text-red-800' };
    case '7':
      return { label: 'Modified', classes: 'bg-yellow-100 text-yellow-800' };
    case '8':
      return { label: 'Cancelled', classes: 'bg-red-100 text-red-800' };
    case '9':
      return { label: 'Not Blocked', classes: 'bg-gray-100 text-gray-800' };
    case '10':
      return { label: 'Blocked', classes: 'bg-purple-100 text-purple-800' };
    case '12':
      return { label: 'No Show', classes: 'bg-red-100 text-red-800' };
    case '13':
      return { label: 'Quote Sent', classes: 'bg-blue-100 text-blue-800' };
    default:
      return { label: 'Unknown', classes: 'bg-gray-100 text-gray-800' };
  }
};

const isCurrentGuest = (checkIn, checkOut, isCancelled) => {
  if (isCancelled) return false;
  const today = new Date();
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  return checkInDate <= today && checkOutDate >= today;
};

const isDateSameDay = (dateValue, referenceDate) => {
  const d = startOfDay(new Date(dateValue));
  const r = startOfDay(referenceDate);
  return d.getTime() === r.getTime();
};

const ReservationCard = ({ reservation, onEdit, onCreateWorkOrder, selectedDate }) => {
  const status = getStatusDisplay(reservation.status_code, reservation.status_id, reservation.is_cancelled);
  const isCancelled = reservation.is_cancelled;

  const isCheckingIn = !isCancelled && isDateSameDay(reservation.check_in, selectedDate);
  const isCheckingOut = !isCancelled && isDateSameDay(reservation.check_out, selectedDate);

  const leftBorderColor = isCancelled
    ? ''
    : isCheckingIn && isCheckingOut
      ? 'border-l-4 border-l-purple-500'
      : isCheckingIn
        ? 'border-l-4 border-l-blue-500'
        : isCheckingOut
          ? 'border-l-4 border-l-amber-500'
          : '';

  return (
    <div
      className={`
        rounded-lg border bg-white p-4 transition-all hover:shadow-sm
        ${isCancelled ? 'opacity-50' : ''}
        ${leftBorderColor}
        ${leftBorderColor ? 'border-t border-r border-b border-gray-200' : 'border-gray-200 hover:border-gray-300'}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Row 1: Guest name + badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`font-medium text-gray-900 ${isCancelled ? 'line-through' : ''}`}>
              {reservation.guest_name}
            </span>
            {isCheckingIn && isCheckingOut && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Same-Day Turnover
              </span>
            )}
            {isCheckingIn && !isCheckingOut && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                </svg>
                Checking In
              </span>
            )}
            {isCheckingOut && !isCheckingIn && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l4 4m0 0l-4 4m4-4H3" />
                </svg>
                Checking Out
              </span>
            )}
            <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${status.classes}`}>
              {status.label}
            </span>
            <ReservationTypeBadge reservation={reservation} context="reservations" />
          </div>

          {/* Row 2: Property info */}
          <div className="mt-1.5 text-sm text-gray-600">
            <span className="font-medium">{reservation.property?.name}</span>
            <span className="text-gray-400 mx-1.5">·</span>
            <span className="text-gray-500">
              {reservation.property?.address}
              {reservation.property?.city && `, ${reservation.property.city}`}
              {reservation.property?.state && `, ${reservation.property.state}`}
              {reservation.property?.zip && ` ${reservation.property.zip}`}
            </span>
          </div>

          {/* Row 3: Dates */}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            <span className={isCheckingIn ? 'text-blue-600 font-medium' : ''}>
              <span className={isCheckingIn ? 'text-blue-400' : 'text-gray-400'}>In:</span>{' '}
              {formatReservationDate(
                reservation.check_in,
                reservation.override_check_in,
                reservation.check_in_time,
                reservation.override_check_in_time,
                reservation.has_time_override
              )}
            </span>
            <span className="text-gray-300">→</span>
            <span className={isCheckingOut ? 'text-amber-600 font-medium' : ''}>
              <span className={isCheckingOut ? 'text-amber-400' : 'text-gray-400'}>Out:</span>{' '}
              {formatReservationDate(
                reservation.check_out,
                reservation.override_check_out,
                reservation.check_out_time,
                reservation.override_check_out_time,
                reservation.has_time_override
              )}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-shrink-0 items-center gap-1">
          {!isCancelled && (
            <button
              onClick={() => onCreateWorkOrder(reservation)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              aria-label={`Create work order for ${reservation.property?.name}`}
              tabIndex={0}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Work Order
            </button>
          )}
          <button
            onClick={() => onEdit(reservation)}
            className="p-2 text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
            aria-label={`Edit reservation for ${reservation.guest_name}`}
            title="Edit Reservation"
            tabIndex={0}
          >
            <EditIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Reservations = () => {
  const todayDate = useMemo(() => startOfDay(new Date()), []);
  const [selectedDate, setSelectedDate] = useState(todayDate);
  const [weekStart, setWeekStart] = useState(() => subDays(todayDate, 3));
  const [selectedAreaGroup, setSelectedAreaGroup] = useState('all');
  const [isAreaDropdownOpen, setIsAreaDropdownOpen] = useState(false);

  const [page, setPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [workOrderReservation, setWorkOrderReservation] = useState(null);
  const [isWorkOrderModalOpen, setIsWorkOrderModalOpen] = useState(false);

  const [hideCancelledReservations, setHideCancelledReservations] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [userRole, setUserRole] = useState('ADMIN');

  const searchInputRef = useRef(null);
  const areaDropdownRef = useRef(null);
  const datePickerRef = useRef(null);

  const { user, loading: authLoading } = useAuth();

  const selectedAreaValues = useMemo(() => {
    const group = AREA_GROUPS.find(g => g.id === selectedAreaGroup);
    return group?.values || [];
  }, [selectedAreaGroup]);

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

  const visibleDays = useMemo(() => {
    return Array.from({ length: DAYS_VISIBLE }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  // Reset page and status filter when filters change
  useEffect(() => {
    setPage(1);
    setActiveFilter(null);
  }, [selectedDate, selectedAreaGroup, hideCancelledReservations]);

  // --- Data fetching ---

  const { loading: dayLoading, error: dayError, data: dayData, refetch } = useQuery(
    GET_RESERVATIONS_BY_DATE,
    {
      variables: {
        input: {
          date: selectedDateStr,
          page,
          limit: RESERVATIONS_PER_PAGE,
          areas: selectedAreaValues.length > 0 ? selectedAreaValues : undefined,
          hideCancelled: hideCancelledReservations,
        },
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      skip: isSearching,
    }
  );

  const [searchReservations, { loading: searchLoading, error: searchError, data: searchData }] = useLazyQuery(
    SEARCH_RESERVATIONS,
    { fetchPolicy: 'network-only' }
  );

  // --- Derived data ---

  const dayReservations = dayData?.reservationsByDate?.reservations || [];
  const dayTotal = dayData?.reservationsByDate?.total || 0;
  const totalPages = Math.ceil(dayTotal / RESERVATIONS_PER_PAGE);

  const { checkingInCount, checkingOutCount, currentCount, filteredDayReservations } = useMemo(() => {
    let inCount = 0;
    let outCount = 0;
    let stayCount = 0;

    dayReservations.forEach(r => {
      const isIn = isDateSameDay(r.check_in, selectedDate);
      const isOut = isDateSameDay(r.check_out, selectedDate);
      if (isIn) inCount++;
      if (isOut) outCount++;
      if (!isIn && !isOut) stayCount++;
    });

    let filtered = dayReservations;
    if (activeFilter === 'checking_in') {
      filtered = dayReservations.filter(r => isDateSameDay(r.check_in, selectedDate));
    } else if (activeFilter === 'checking_out') {
      filtered = dayReservations.filter(r => isDateSameDay(r.check_out, selectedDate));
    } else if (activeFilter === 'current') {
      filtered = dayReservations.filter(r => !isDateSameDay(r.check_in, selectedDate) && !isDateSameDay(r.check_out, selectedDate));
    }

    return {
      checkingInCount: inCount,
      checkingOutCount: outCount,
      currentCount: stayCount,
      filteredDayReservations: filtered,
    };
  }, [dayReservations, selectedDate, activeFilter]);

  const searchResults = useMemo(() => {
    if (!isSearching || !searchData?.searchReservations) return [];
    let results = [...searchData.searchReservations];
    if (hideCancelledReservations) {
      results = results.filter(r => !r.is_cancelled);
    }
    return results.sort((a, b) => new Date(a.check_in) - new Date(b.check_in));
  }, [searchData, isSearching, hideCancelledReservations]);

  // --- Day strip navigation ---

  const handlePrevDays = () => setWeekStart(prev => subDays(prev, DAYS_VISIBLE));
  const handleNextDays = () => setWeekStart(prev => addDays(prev, DAYS_VISIBLE));

  const handleSelectDay = (date) => {
    setSelectedDate(date);
  };

  const handleGoToToday = () => {
    const t = startOfDay(new Date());
    setSelectedDate(t);
    setWeekStart(subDays(t, 3));
  };

  const handleDatePickerChange = (e) => {
    const dateStr = e.target.value;
    if (!dateStr) return;
    const [year, month, day] = dateStr.split('-').map(Number);
    const newDate = startOfDay(new Date(year, month - 1, day));
    setSelectedDate(newDate);
    setWeekStart(subDays(newDate, 3));
  };

  // --- Search handlers ---

  const debouncedGetSuggestions = useMemo(
    () =>
      debounce((term) => {
        if (term && term.length >= 2) {
          setIsLoadingSuggestions(true);
          searchReservations({ variables: { searchTerm: term } })
            .then(({ data }) => {
              if (data?.searchReservations) {
                const suggestions = [];
                const seenProperties = new Set();
                const seenGuests = new Set();
                data.searchReservations.slice(0, 10).forEach(reservation => {
                  if (reservation.property?.name && !seenProperties.has(reservation.property.name)) {
                    suggestions.push({
                      type: 'property',
                      value: reservation.property.name,
                      label: reservation.property.name,
                      sublabel: `${reservation.property.address}, ${reservation.property.city}`,
                      id: reservation.property.id,
                    });
                    seenProperties.add(reservation.property.name);
                  }
                  if (reservation.guest_name && !seenGuests.has(reservation.guest_name)) {
                    suggestions.push({
                      type: 'guest',
                      value: reservation.guest_name,
                      label: reservation.guest_name,
                      sublabel: `${reservation.property?.name} · ${format(new Date(reservation.check_in), 'MMM d, yyyy')}`,
                      id: reservation.id,
                    });
                    seenGuests.add(reservation.guest_name);
                  }
                });
                setSearchSuggestions(suggestions.slice(0, 8));
                setShowSuggestions(true);
              }
              setIsLoadingSuggestions(false);
            })
            .catch(() => setIsLoadingSuggestions(false));
        } else {
          setSearchSuggestions([]);
          setShowSuggestions(false);
          setIsLoadingSuggestions(false);
        }
      }, 800),
    [searchReservations]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (term && term.length >= 3) {
          setIsSearching(true);
          setShowSuggestions(false);
          searchReservations({ variables: { searchTerm: term } });
        } else {
          setIsSearching(false);
        }
      }, 1200),
    [searchReservations]
  );

  useEffect(() => {
    if (searchTerm.length >= 2 && searchTerm.length < 3) {
      debouncedGetSuggestions(searchTerm);
      debouncedSearch.cancel();
    } else {
      debouncedSearch(searchTerm);
      debouncedGetSuggestions.cancel();
      if (searchTerm.length < 2) {
        setShowSuggestions(false);
        setSearchSuggestions([]);
      }
    }
    return () => {
      debouncedSearch.cancel();
      debouncedGetSuggestions.cancel();
    };
  }, [searchTerm, debouncedSearch, debouncedGetSuggestions]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsSearching(false);
    setShowSuggestions(false);
    setSearchSuggestions([]);
  };

  const handleSuggestionSelect = (suggestion) => {
    setSearchTerm(suggestion.value);
    setShowSuggestions(false);
    setIsSearching(true);
    searchReservations({ variables: { searchTerm: suggestion.value } });
  };

  // --- Edit handlers ---

  const handleEditClick = (reservation) => {
    if (!reservation) return;
    const reservationWithProperty = {
      ...reservation,
      property: {
        ...(reservation.property || {}),
        id: reservation.property?.id || reservation.property_id,
        name: reservation.property?.name || 'Unknown Property',
      },
    };
    setSelectedReservation(reservationWithProperty);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedReservation(null);
    setIsEditModalOpen(false);
    refetch();
  };

  const handleCreateWorkOrder = (reservation) => {
    if (!reservation) return;
    setWorkOrderReservation(reservation);
    setIsWorkOrderModalOpen(true);
  };

  const handleWorkOrderModalClose = (wasSuccessful) => {
    setIsWorkOrderModalOpen(false);
    setWorkOrderReservation(null);
    if (wasSuccessful) {
      toast.success('Work order created successfully');
    }
  };

  // --- Click outside ---

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(event.target)) {
        setIsAreaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Pagination ---

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // --- Loading / error states ---

  const loading = dayLoading || searchLoading;
  const error = dayError || searchError;

  const reservations = isSearching ? searchResults : filteredDayReservations;

  // --- Render ---

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="my-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-5">
          <h1 className="text-2xl font-normal text-white lg:text-gray-900">
            Property Reservations
          </h1>
          {!isSameDay(selectedDate, todayDate) && !isSearching && (
            <button
              onClick={handleGoToToday}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
              aria-label="Go to today"
              tabIndex={0}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Today
            </button>
          )}
        </div>

        {/* Day navigation strip */}
        {!isSearching && (
          <div className="mb-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex items-center gap-1">
              {/* Previous arrow */}
              <button
                onClick={handlePrevDays}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Previous days"
                tabIndex={0}
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>

              {/* Day pills */}
              <div className="flex-1 grid grid-cols-7 gap-1">
                {visibleDays.map((day) => {
                  const isSelected = isSameDay(day, selectedDate);
                  const isTodayDay = isToday(day);
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => handleSelectDay(day)}
                      className={`
                        relative flex flex-col items-center py-2 px-1 rounded-lg transition-all text-center
                        ${isSelected
                          ? 'bg-blue-600 text-white shadow-sm'
                          : isTodayDay
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            : 'text-gray-600 hover:bg-gray-100'
                        }
                      `}
                      aria-label={format(day, 'EEEE, MMMM d, yyyy')}
                      aria-pressed={isSelected}
                      tabIndex={0}
                    >
                      <span className={`text-xs font-medium uppercase tracking-wide ${isSelected ? 'text-blue-100' : 'text-gray-400'}`}>
                        {format(day, 'EEE')}
                      </span>
                      <span className={`text-lg font-semibold leading-tight mt-0.5 ${isSelected ? 'text-white' : ''}`}>
                        {format(day, 'd')}
                      </span>
                      <span className={`text-[10px] mt-0.5 ${isSelected ? 'text-blue-200' : 'text-gray-400'}`}>
                        {format(day, 'MMM')}
                      </span>
                      {isTodayDay && !isSelected && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Next arrow */}
              <button
                onClick={handleNextDays}
                className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Next days"
                tabIndex={0}
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>

              {/* Date picker */}
              <div className="flex-shrink-0 relative ml-1">
                <input
                  ref={datePickerRef}
                  type="date"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  value={selectedDateStr}
                  onChange={handleDatePickerChange}
                  aria-label="Pick a date"
                  tabIndex={0}
                />
                <div className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors pointer-events-none">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters row */}
        <div className="mb-5 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search bar */}
            <div className="flex-1 relative" ref={searchInputRef}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by guest name or property..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (searchSuggestions.length > 0) setShowSuggestions(true);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CA3AF' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3B82F6', borderWidth: '2px' },
                  },
                  '& .MuiInputBase-input': { fontSize: '14px', fontWeight: '400' },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className="text-gray-400" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isLoadingSuggestions && <CircularProgress size={18} className="mr-2 text-blue-500" />}
                      {searchTerm && (
                        <IconButton
                          aria-label="clear search"
                          onClick={handleClearSearch}
                          edge="end"
                          size="small"
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Search suggestions dropdown */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <Paper
                  elevation={4}
                  className="absolute top-full left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto"
                  style={{ backgroundColor: 'white' }}
                >
                  <div className="p-2">
                    <Typography variant="caption" className="text-gray-500 px-2 py-1 block">
                      Quick suggestions (click to search):
                    </Typography>
                    <Divider className="my-1" />
                    {searchSuggestions.map((suggestion, index) => (
                      <div
                        key={`${suggestion.type}-${suggestion.id}-${index}`}
                        className="flex items-center p-2 hover:bg-gray-50 cursor-pointer rounded"
                        onClick={() => handleSuggestionSelect(suggestion)}
                        role="option"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSuggestionSelect(suggestion); }}
                      >
                        <div className="flex-shrink-0 mr-3">
                          {suggestion.type === 'property' ? (
                            <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5a2 2 0 11-4 0 2 2 0 014 0zM3 21h18" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{suggestion.label}</p>
                          <p className="text-xs text-gray-500 truncate">{suggestion.sublabel}</p>
                        </div>
                      </div>
                    ))}
                    {searchTerm.length >= 2 && searchTerm.length < 3 && (
                      <>
                        <Divider className="my-2" />
                        <div className="px-2 py-1">
                          <Typography variant="caption" className="text-gray-500">
                            Type {3 - searchTerm.length} more character{3 - searchTerm.length > 1 ? 's' : ''} for full search results
                          </Typography>
                        </div>
                      </>
                    )}
                  </div>
                </Paper>
              )}
            </div>

            {/* Area filter dropdown */}
            <div className="w-full md:w-56 relative" ref={areaDropdownRef}>
              <button
                type="button"
                onClick={() => setIsAreaDropdownOpen(!isAreaDropdownOpen)}
                className="w-full h-12 flex items-center justify-between px-4 border border-gray-300 rounded-xl bg-white hover:bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                aria-label="Filter by area"
                aria-expanded={isAreaDropdownOpen}
                tabIndex={0}
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className={selectedAreaGroup === 'all' ? 'text-gray-500' : 'font-medium text-gray-900'}>
                    {AREA_GROUPS.find(g => g.id === selectedAreaGroup)?.label}
                  </span>
                </span>
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${isAreaDropdownOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {isAreaDropdownOpen && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white shadow-xl rounded-lg py-1 text-sm ring-1 ring-gray-200 overflow-hidden">
                  {AREA_GROUPS.map(group => (
                    <button
                      key={group.id}
                      type="button"
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        selectedAreaGroup === group.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => {
                        setSelectedAreaGroup(group.id);
                        setIsAreaDropdownOpen(false);
                      }}
                    >
                      <span className={selectedAreaGroup === group.id ? 'font-medium' : ''}>{group.label}</span>
                      {selectedAreaGroup === group.id && (
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cancelled toggle */}
            <label className="flex items-center cursor-pointer flex-shrink-0 h-12">
              <input
                type="checkbox"
                checked={hideCancelledReservations}
                onChange={(e) => setHideCancelledReservations(e.target.checked)}
                className="sr-only"
              />
              <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 ${
                hideCancelledReservations ? 'bg-blue-600' : 'bg-gray-200'
              }`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  hideCancelledReservations ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </div>
              <span className="ml-2 text-sm text-gray-300 lg:text-gray-600 whitespace-nowrap">
                Hide cancelled
              </span>
            </label>
          </div>

          {/* Status line + filter bubbles */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="text-sm text-gray-300 md:text-gray-500">
              {isSearching && searchTerm.length >= 3 ? (
                <span>
                  <span className="font-semibold text-gray-700">{searchResults.length}</span> result{searchResults.length !== 1 ? 's' : ''} for{' '}
                  <span className="font-medium text-gray-700">"{searchTerm}"</span>
                </span>
              ) : (
                <span>
                  <span className="font-semibold text-gray-700">{dayTotal}</span> reservation{dayTotal !== 1 ? 's' : ''}{' '}
                  <span className="text-gray-400">·</span>{' '}
                  <span className="font-medium text-gray-700">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                  {selectedAreaGroup !== 'all' && (
                    <span className="text-blue-600 ml-2">
                      · {AREA_GROUPS.find(g => g.id === selectedAreaGroup)?.label}
                    </span>
                  )}
                </span>
              )}
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className="text-amber-600 ml-2">
                  · Type {3 - searchTerm.length} more character{3 - searchTerm.length > 1 ? 's' : ''} for search
                </span>
              )}
            </div>

            {/* Filter bubbles */}
            {!isSearching && dayReservations.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveFilter(activeFilter === 'checking_in' ? null : 'checking_in')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeFilter === 'checking_in'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                  aria-label="Filter to checking in"
                  aria-pressed={activeFilter === 'checking_in'}
                  tabIndex={0}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                  </svg>
                  Checking In
                  <span className={`ml-0.5 ${activeFilter === 'checking_in' ? 'text-blue-200' : 'text-blue-400'}`}>
                    {checkingInCount}
                  </span>
                </button>
                <button
                  onClick={() => setActiveFilter(activeFilter === 'checking_out' ? null : 'checking_out')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeFilter === 'checking_out'
                      ? 'bg-amber-500 text-white'
                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  }`}
                  aria-label="Filter to checking out"
                  aria-pressed={activeFilter === 'checking_out'}
                  tabIndex={0}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  Checking Out
                  <span className={`ml-0.5 ${activeFilter === 'checking_out' ? 'text-amber-200' : 'text-amber-400'}`}>
                    {checkingOutCount}
                  </span>
                </button>
                <button
                  onClick={() => setActiveFilter(activeFilter === 'current' ? null : 'current')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeFilter === 'current'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  }`}
                  aria-label="Filter to current guests"
                  aria-pressed={activeFilter === 'current'}
                  tabIndex={0}
                >
                  Current
                  <span className={`ml-0.5 ${activeFilter === 'current' ? 'text-emerald-200' : 'text-emerald-400'}`}>
                    {currentCount}
                  </span>
                </button>
                {activeFilter && (
                  <button
                    onClick={() => setActiveFilter(null)}
                    className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label="Clear filter"
                    tabIndex={0}
                  >
                    <ClearIcon className="w-4 h-4" style={{ fontSize: 16 }} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {loading && !dayData && !searchData ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <CircularProgress />
          </div>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button onClick={() => refetch()} size="small" color="inherit">
                Retry
              </Button>
            }
          >
            Error loading reservations. Please try again.
          </Alert>
        ) : reservations.length === 0 ? (
          <div className="text-center py-16">
            <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-3 text-sm font-medium text-gray-600">No reservations</h3>
            <p className="mt-1 text-sm text-gray-400">
              {isSearching
                ? `No results found for "${searchTerm}".`
                : `No active reservations for ${format(selectedDate, 'MMMM d, yyyy')}.`}
            </p>
            {!isSearching && (
              <Button
                component={Link}
                to="/settings"
                size="small"
                className="mt-4"
                variant="text"
              >
                Sync with Streamline
              </Button>
            )}
          </div>
        ) : (
          <>
            {loading && (
              <div className="flex justify-center py-4">
                <CircularProgress size={24} />
              </div>
            )}
            <div className="space-y-2">
              {reservations.map((reservation) => (
              <ReservationCard
                key={reservation.id}
                reservation={reservation}
                onEdit={handleEditClick}
                onCreateWorkOrder={handleCreateWorkOrder}
                selectedDate={selectedDate}
              />
              ))}
            </div>
            {!isSearching && totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages} · Showing {(page - 1) * RESERVATIONS_PER_PAGE + 1}–{Math.min(page * RESERVATIONS_PER_PAGE, dayTotal)} of {dayTotal}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="First page"
                    tabIndex={0}
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                    tabIndex={0}
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                      ) : (
                        <button
                          key={item}
                          onClick={() => handlePageChange(item)}
                          className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
                            item === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                          aria-label={`Page ${item}`}
                          aria-current={item === page ? 'page' : undefined}
                          tabIndex={0}
                        >
                          {item}
                        </button>
                      )
                    )}
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                    tabIndex={0}
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-sm text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Last page"
                    tabIndex={0}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <EditReservationModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        reservation={selectedReservation}
        refetch={refetch}
        userRole={userRole}
      />

      <CreateWorkOrderModal
        isOpen={isWorkOrderModalOpen}
        onClose={handleWorkOrderModalClose}
        propertyId={workOrderReservation?.property?.id}
        propertyName={workOrderReservation?.property?.name}
        reservationId={workOrderReservation?.id}
      />
    </div>
  );
};

export default Reservations;
